import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiMail, FiPhone, FiUser, FiSearch } from 'react-icons/fi';

const Teachers = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  
  // Search filters
  const [filters, setFilters] = useState({
    role: '',
    search: ''
  });

  useEffect(() => {
    fetchTeachers();
    fetchRoles();
  }, []);

  const fetchTeachers = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const response = await teacherAPI.getTeachers(searchFilters);
      if (response.data.success) {
        setTeachers(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching teachers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await teacherAPI.getFormData();
      if (response.data.success) {
        setRoles(response.data.data.roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTeachers(filters);
  };

  const handleRoleFilter = (e) => {
    const roleId = e.target.value;
    setFilters({ ...filters, role: roleId });
    fetchTeachers({ role: roleId, search: filters.search });
  };

  const handleDelete = async (teacherId, teacherName) => {
    if (!window.confirm(`Are you sure you want to remove ${teacherName}? This will disable the teacher.`)) {
      return;
    }

    try {
      const response = await teacherAPI.deleteTeacher(teacherId);
      if (response.data.success) {
        toast.success('Teacher removed successfully');
        fetchTeachers(filters);
      }
    } catch (error) {
      toast.error('Error removing teacher');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Human Resource</h1>
          <p className="text-sm md:text-base text-gray-600">Manage your institute's teaching staff</p>
        </div>
        <button 
          onClick={() => navigate('/teachers/create')}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm md:text-base min-h-[44px] w-full sm:w-auto"
        >
          <FiPlus /> Add Staff
        </button>
      </div>

      {/* Search Filters - exactly like admin */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiSearch /> Select Criteria
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Role Filter */}
          <div>
            <form onSubmit={(e) => { e.preventDefault(); handleRoleFilter({ target: { value: filters.role } }); }}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={filters.role}
                onChange={handleRoleFilter}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              >
                <option value="">Select</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </form>
          </div>

          {/* Keyword Search */}
          <div>
            <form onSubmit={handleSearch}>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Search By Keyword
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search by Staff ID, Name, Email..."
                  className="flex-1 px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none text-sm md:text-base"
                />
                <button
                  type="submit"
                  className="px-4 md:px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] flex items-center justify-center"
                >
                  <FiSearch className="md:mr-2" />
                  <span className="hidden md:inline">Search</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {(filters.role || filters.search) && (
          <button
            onClick={() => {
              setFilters({ role: '', search: '' });
              fetchTeachers();
            }}
            className="mt-4 text-xs md:text-sm text-primary-600 hover:text-primary-700 min-h-[44px] flex items-center"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table View - exactly like admin */}
      {teachers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 md:py-16 bg-gray-50 rounded-lg">
          <FiUser size={48} className="md:w-16 md:h-16 text-gray-300 mb-4" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">No teachers found</h3>
          <p className="text-sm md:text-base text-gray-500 mb-6">Add your first teacher to get started</p>
          <button 
            onClick={() => navigate('/teachers/create')}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium min-h-[44px]"
          >
            <FiPlus /> Add Staff
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Staff ID</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Designation</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mobile Number</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Commission</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Discount</th>
                  <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <tr key={teacher.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-900">{teacher.employee_id}</td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2 md:gap-3">
                        {teacher.image ? (
                          <img 
                            src={`http://localhost/brainershub/${teacher.image}`} 
                            alt={teacher.name}
                            className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className={`w-9 h-9 md:w-10 md:h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm ${teacher.image ? 'hidden' : 'flex'}`}>
                          {teacher.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs md:text-sm font-medium text-gray-900">
                            {teacher.name} {teacher.surname || ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.role_name || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.department_name || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.designation_name || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.contact_no || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.commision || 0}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{teacher.discount || 0}</td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex justify-end items-center gap-1 md:gap-2">
                        <button
                          onClick={() => navigate(`/teachers/${teacher.id}/edit`)}
                          className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(teacher.id, `${teacher.name} ${teacher.surname || ''}`)}
                          className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teachers;
